import React, { useState, useEffect } from "react";
import { FaCheck, FaEdit, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicSelect from "../DynamicSelect";
import ExportOptions from "../ExportOptions";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";
import PageHeading from "../PageHeading";

// Modal for adding/editing asset value
const AssetValueModal = ({ isOpen, onClose, onSave, assets, asset }) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetValue, setAssetValue] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Prepare options for DynamicSelect
  const assetOptions = assets.map((a) => ({
    value: a.id,
    label: a.assetName,
  }));

  useEffect(() => {
    if (isOpen) {
      if (asset) {
        // Edit mode: Pre-select the asset and its value
        setSelectedAsset({ value: asset.id, label: asset.assetName });
        setAssetValue(asset.assetValue.toString());
      } else {
        // Add mode: Clear fields
        setSelectedAsset(null);
        setAssetValue("");
      }
      setErrors({});
    }
  }, [isOpen, asset, assets]);

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!selectedAsset && !asset) {
      newErrors.assetName = "Asset Name is required";
    }
    if (!assetValue.trim()) {
      newErrors.assetValue = "Asset Value is required";
    } else if (!/^\d+$/.test(assetValue) || parseInt(assetValue) < 0) {
      newErrors.assetValue = "Asset Value must be a non-negative integer";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleAssetChange = (selectedOption, actionMeta) => {
    if (actionMeta.action === "create-option") {
      // Handle new asset name input
      setSelectedAsset({ value: null, label: selectedOption.label });
      setAssetValue("");
    } else {
      // Handle existing asset selection
      setSelectedAsset(selectedOption);
      const selectedAssetData = assets.find((a) => a.id === selectedOption?.value);
      setAssetValue(selectedAssetData ? selectedAssetData.assetValue.toString() : "");
    }
    setErrors((prev) => ({ ...prev, assetName: "" }));
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (selectedAsset && selectedAsset.value) {
        // Edit mode: Update existing asset via PUT
        const response = await fetch(`https://api.muktihospital.com/api/assets/edit/${selectedAsset.value}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
          body: JSON.stringify({
            assetName: selectedAsset.label,
            assetValue: parseInt(assetValue),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update asset");
        }

        const { asset: updatedAsset } = await response.json();
        onSave(selectedAsset.value, updatedAsset);
        toast.success("Asset value updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        // Add mode: Create new asset via POST
        const response = await fetch("https://api.muktihospital.com/api/assets/add", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
          body: JSON.stringify({
            assetName: selectedAsset.label.trim(),
            assetValue: parseInt(assetValue),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add asset");
        }

        const { asset: newAsset } = await response.json();
        onSave(null, newAsset);
        toast.success("Asset added successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      setSelectedAsset(null);
      setAssetValue("");
      setErrors({});
      onClose();
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error(error.message || "Error saving asset!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden p-2">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-700">
            {asset ? "Edit Asset Value" : "Add Asset Value"}
          </h2>
        </div>
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Asset Name <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-2 mb-4">
            <div className="col-span-5">
              <DynamicSelect
                options={assetOptions}
                onChange={handleAssetChange}
                value={selectedAsset}
                isSearchable={true}
                isCreatable={true} // Allow creating new options
                placeholder="Select or type asset name"
                controlBackgroundColor="#f7fafc"
                controlFontSize="14px"
                controlBorderColor={errors.assetName ? "#ef4444" : "#cbd5e0"}
                isDisabled={isLoading}
              />
              {errors.assetName && (
                <span className="text-red-500 text-xs mt-1">{errors.assetName}</span>
              )}
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Asset Value <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={assetValue}
            onChange={(e) => {
              setAssetValue(e.target.value);
              if (e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, assetValue: "" }));
              }
            }}
            className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
              errors.assetValue ? "border-red-500" : ""
            }`}
            placeholder="Asset Value"
            disabled={isLoading}
          />
          {errors.assetValue && (
            <span className="text-red-500 text-xs mt-1">{errors.assetValue}</span>
          )}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TotalAssets = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assetValueModalOpen, setAssetValueModalOpen] = useState(false);
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;

  // Fetch assets from backend
  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://api.muktihospital.com/api/assets", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch assets");
      }

      const { assets } = await response.json();
      // console.log("Fetched assets:", assets); // Debug log
      const formattedAssets = assets.map((asset) => ({
        id: asset.id,
        assetName: asset.assetName,
        assetValue: asset.assetValue || 0,
        quantity: asset.quantity ?? 0,
      }));
      setAssets(formattedAssets);
    } catch (error) {
      console.error("Error fetching assets:", error);
      toast.error(error.message || "Error fetching assets!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch assets on component mount
  useEffect(() => {
    fetchAssets();
  }, []);

  const filteredAssets = assets.filter((asset) =>
    asset.assetName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  const currentData = filteredAssets
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((asset, index) => ({
      ...asset,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((asset) => asset.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (assetId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(assetId)
        ? prevSelectedRows.filter((id) => id !== assetId)
        : [...prevSelectedRows, assetId]
    );
  };

  const handleDelete = async (assetIds) => {
    const idsToDelete = Array.isArray(assetIds) ? assetIds : [assetIds];
    setLoading(true);
    try {
      for (const id of idsToDelete) {
        const response = await fetch(`https://api.muktihospital.com/api/assets/delete/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to delete asset with ID ${id}`);
        }
      }

      // Refetch assets to sync with backend
      await fetchAssets();
      setSelectedRows([]);
      toast.success(`${idsToDelete.length} asset(s) deleted successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error deleting assets:", error);
      toast.error(error.message || "Error deleting asset(s)!", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const handleSaveAssetValue = async (id, data) => {
    if (id) {
      // Edit mode: Update local state
      setAssets(
        assets.map((asset) =>
          asset.id === id ? { ...asset, assetName: data.assetName, assetValue: data.assetValue } : asset
        )
      );
    } else {
      // Add mode: Append new asset
      setAssets([data, ...assets]);
    }
    await fetchAssets(); // Refetch to ensure consistency
  };

  const handleSelect = (asset) => {
    setAssetToEdit(asset);
    setAssetValueModalOpen(true);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Store Management" },
    { label: "Total Assets" },
  ];


  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Database ID", "Asset Name", "Stock (Qty)"];
  const dataMapper = (item, index) =>
    [
      item.displayId ||
        (index !== undefined
          ? (currentPage - 1) * itemsPerPage + index + 1
          : item.id),
      item.assetName || "",
      item.assetValue || 0,
    ].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "100px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "200px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "80px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden">
        <PageHeading title="Total Assets" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Total Assets List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by asset name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setAssetToEdit(null);
                  setAssetValueModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Asset Value
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Total Assets"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Database ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Value
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No assets found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              currentData.length > 0 &&
                              currentData.every((item) =>
                                selectedRows.includes(item.id)
                              )
                            }
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Database ID
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Name
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Asset Value
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((asset, index) => (
                      <tr
                        key={asset.id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`asset-${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(asset.id)}
                              onChange={() => handleRowSelect(asset.id)}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={`asset-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {asset.displayId}
                        </td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {asset.assetName}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {asset.quantity ?? 0}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleSelect(asset)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([asset.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                  {Math.min(
                    currentPage * itemsPerPage,
                    filteredAssets.length
                  )}{" "}
                  of {filteredAssets.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button
                      className="table-pagination-item disabled:opacity-50"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10
                    ? [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1
                                ? "bg-M-primary-color text-white"
                                : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    : (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;

                        for (
                          let i = 1;
                          i <= Math.min(firstPages, totalPages);
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li
                              key="ellipsis"
                              className="table-pagination-item"
                            >
                              ...
                            </li>
                          );
                        }

                        for (
                          let i = Math.max(
                            firstPages + 1,
                            totalPages - lastPages + 1
                          );
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i
                                    ? "bg-M-primary-color text-white"
                                    : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }

                        return pages;
                      })()}
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="asset"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} asset(s)?`
            : `Do you really want to delete this asset?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <AssetValueModal
        isOpen={assetValueModalOpen}
        onClose={() => {
          setAssetToEdit(null);
          setAssetValueModalOpen(false);
        }}
        onSave={handleSaveAssetValue}
        assets={assets}
        asset={assetToEdit}
      />
    </div>
  );
};

export default TotalAssets;